import { APP_TO_BUILD } from "../../appsConfig";
import {
  API_BASE_URL,
  clearClerkSession,
  getClerkClient,
  getFreshAuthToken,
} from "@/lib/useAuthedFetch";
import { RequestTimeoutError, raceAbort } from "@/lib/requestTimeout";
import { getDeviceId } from "@/utils/identity";
import type { OrgSeatPrice } from "@/utils/orgPurchases/config";
import {
  type DistributionAccessEntry,
  type DistributionAccessRole,
  type ManagedDistributionSessionResponse,
  normalizeCode,
  normalizeEmail,
  type OrgExamType,
  type RedemptionResult,
} from "@petergok/blue-collar-shared";
import type {
  DistributionBatchSendRequest,
  DistributionBatchSendResponse,
  ManagedDistributionCodeWithStatus,
} from "@/types/distributionBatch";
import {
  OrgManagementApiError,
  readManagementJson,
} from "./managementResponse";
export {
  isManagementAuthorizationError,
  isManagementPageAccessError,
  OrgManagementApiError,
  readManagementJson,
} from "./managementResponse";

export interface CreateOrgCheckoutSessionPayload {
  examType: OrgExamType;
  seatCount?: number;
}

export interface CreateOrgCheckoutSessionResponse {
  sessionId: string;
  url: string | null;
}

type ReadyDistributionSession = Extract<
  ManagedDistributionSessionResponse,
  { status: "ready" }
>;

// The backend returns the queued-send fields on each ready code; they are modeled
// locally (see @/types/distributionBatch) rather than in the shared package.
export type DistributionSessionResponse =
  | Exclude<ManagedDistributionSessionResponse, { status: "ready" }>
  | (Omit<ReadyDistributionSession, "codes"> & {
      codes: ManagedDistributionCodeWithStatus[];
    });

export type RedeemOrgAccessCodeResult =
  RedemptionResult | { ok: false; reason: "invalid_input" | "rate_limited" };

async function readJson(response: Response): Promise<unknown> {
  const body = await response.text();

  try {
    return body ? JSON.parse(body) : null;
  } catch {
    if (!response.ok) {
      throw new Error(body || `${response.status} ${response.statusText}`);
    }

    throw new Error("Response was not valid JSON.");
  }
}

async function readJsonOrThrow<T>(response: Response): Promise<T> {
  const json = (await readJson(response)) as {
    error?: unknown;
    reason?: unknown;
  } | null;

  if (!response.ok) {
    const message =
      typeof json?.error === "string"
        ? json.error
        : typeof json?.reason === "string"
          ? json.reason
          : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return json as T;
}

async function getManagementAuthorization(): Promise<string> {
  const clerk = await getClerkClient();
  if (!clerk.session) {
    throw new OrgManagementApiError("authentication_required", 401);
  }
  // Share the app-wide token getter so bulk distribution does not turn one send
  // per seat into one auth round-trip per seat, and so a send reuses the token
  // the page bootstrap just minted instead of racing Clerk's rate limit.
  const token = await getFreshAuthToken();
  if (!token) {
    throw new OrgManagementApiError("authentication_required", 401);
  }
  return `Bearer ${token}`;
}

async function managementFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const authorization = await getManagementAuthorization();
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  headers.set("Authorization", authorization);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });
  return readManagementJson<T>(response);
}

export async function createOrgCheckoutSession(
  payload: CreateOrgCheckoutSessionPayload,
) {
  const response = await fetch(`${API_BASE_URL}/organizations/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return readJsonOrThrow<CreateOrgCheckoutSessionResponse>(response);
}

export async function getOrgSeatPrice(
  examType: OrgExamType,
  signal?: AbortSignal,
): Promise<OrgSeatPrice> {
  const response = await fetch(
    `${API_BASE_URL}/organizations/price/${examType}`,
    { method: "GET", signal },
  );

  return readJsonOrThrow<OrgSeatPrice>(response);
}

export async function getDistributionSession(
  token: string,
  signal?: AbortSignal,
) {
  return managementFetch<DistributionSessionResponse>(
    `/organizations/distribute/${encodeURIComponent(token)}`,
    { method: "GET", signal },
  );
}

export async function sendDistributionCode(
  token: string,
  payload: { code: string; email: string },
) {
  return managementFetch<{ ok: true; sentAt?: number }>(
    `/organizations/distribute/${encodeURIComponent(token)}/send`,
    {
      method: "POST",
      body: JSON.stringify({
        code: normalizeCode(payload.code),
        email: normalizeEmail(payload.email),
      }),
    },
  );
}

export function getDistributionAccess(token: string) {
  return managementFetch<{ people: DistributionAccessEntry[] }>(
    `/organizations/distribute/${encodeURIComponent(token)}/access`,
  );
}

export function addDistributionAccess(
  token: string,
  input: { email: string; role: DistributionAccessRole },
) {
  return managementFetch<{ person: DistributionAccessEntry }>(
    `/organizations/distribute/${encodeURIComponent(token)}/access`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function changeDistributionAccessRole(
  token: string,
  entryId: string,
  role: DistributionAccessRole,
) {
  return managementFetch<{ person: DistributionAccessEntry }>(
    `/organizations/distribute/${encodeURIComponent(token)}/access/${encodeURIComponent(entryId)}`,
    { method: "PATCH", body: JSON.stringify({ role }) },
  );
}

export async function removeDistributionAccess(token: string, entryId: string) {
  await managementFetch<null>(
    `/organizations/distribute/${encodeURIComponent(token)}/access/${encodeURIComponent(entryId)}`,
    { method: "DELETE" },
  );
}

export function reissueDistributionCode(token: string, code: string) {
  return managementFetch<{ ok: true; code: string }>(
    `/organizations/distribute/${encodeURIComponent(token)}/codes/${encodeURIComponent(normalizeCode(code))}/reissue`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function resendDistributionCode(token: string, code: string) {
  return managementFetch<{ ok: true; sentAt: number }>(
    `/organizations/distribute/${encodeURIComponent(token)}/codes/${encodeURIComponent(normalizeCode(code))}/resend`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function sendDistributionCodesBatch(token: string, emails: string[]) {
  const payload: DistributionBatchSendRequest = { emails };
  return managementFetch<DistributionBatchSendResponse>(
    `/organizations/distribute/${encodeURIComponent(token)}/send-batch`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/**
 * How long to wait before giving up on a redemption.
 *
 * This is THE call the non-dismissable "Redeeming…" dialog waits on — the legacy
 * `redeemAccessCode` path is only reached as an `electrical_us` fallback after this one
 * returns `not_found`. Without a deadline here, an org/school learner on a weak signal
 * had no exit but reloading the page.
 */
const REDEEM_TIMEOUT_MS = 20_000;

export async function redeemOrgAccessCode(payload: {
  code: string;
  examType: OrgExamType;
}): Promise<RedeemOrgAccessCodeResult> {
  // Deadline covers the whole operation, including the three preflight steps below.
  // None of them accepts an AbortSignal — the Clerk client handle, the token mint, and
  // the device-id lookup all make their own untimed work — so each is RACED against the
  // signal rather than awaited directly. Testing a `timedOut` flag after the await was
  // not enough: when the await is what never returns, the check is never reached.
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REDEEM_TIMEOUT_MS);

  const withDeadline = <V,>(work: Promise<V>): Promise<V> =>
    raceAbort(
      work,
      controller.signal,
      () => new RequestTimeoutError("/v1/redeem-code", REDEEM_TIMEOUT_MS)
    );

  try {
    const clerk = await withDeadline(getClerkClient());

    if (!clerk.session) {
      throw new Error("No active session. Please sign in.");
    }

    const token = await withDeadline(getFreshAuthToken());
    if (!token) {
      throw new Error("No auth token available.");
    }

    const deviceId = await withDeadline(getDeviceId());

    const response = await fetch(`${API_BASE_URL}/v1/redeem-code`, {
      method: "POST",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        "X-App-Type": APP_TO_BUILD,
        "X-Device-Id": deviceId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        code: normalizeCode(payload.code),
        examType: payload.examType,
      }),
    });

    if (response.status === 401) {
      clearClerkSession();
    }

    const body = await readJson(response);
    if (
      !body ||
      typeof body !== "object" ||
      (!("ok" in body) && !("reason" in body))
    ) {
      throw new Error(`${response.status} ${response.statusText}`);
    }

    return body as RedeemOrgAccessCodeResult;
  } catch (e) {
    if (timedOut) {
      throw new RequestTimeoutError("/v1/redeem-code", REDEEM_TIMEOUT_MS);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}
